import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, InvoiceService, ReportService, UtilityService } from 'app/core/core.module';
import { Guid } from 'app/core/data/entities';
import { Invoice, Order, OrderLineDetail, PackingSlip } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { PackingSlipDialogComponent } from '../packing-slip-dialog/packing-slip-dialog.component';
import { OrderBuildDetailComponent } from 'app/orders/order-build-detail/order-build-detail.component';

@Component({
    selector: 'app-packing-slip-search',
    templateUrl: './packing-slip-search.component.html',
    styleUrls: ['./packing-slip-search.component.scss'],
    standalone: false,
})
export class PackingSlipSearchComponent implements OnInit {
    invoiceService = inject(InvoiceService);
    entitiesService = inject(EntitiesService);
    modalService = inject(BsModalService);
    utility = inject(UtilityService);

    packingSlips: any;
    orderLines: any;
    workOrderList: any[];
    showAllOrders = false;
    filter: any;
    recentData: any;

    salesMode = false;
    ordersMode = false;
    claimButtonDisable = true;
    unclaimButtonDisable = true;
    woAddedSubscription: Subscription;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('recentDataGrid') recentDataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private appInfo: AppInformationService,
        private reportService: ReportService,
    ) {}

    async ngOnInit() {
        this.packingSlips = [];
        this.appInfo.pleaseWaitShow();
        await this.getData();
        await this.getRecentData();
        this.appInfo.pleaseWaitHide();
    }

    async refresh() {
        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async getData() {
        const packingSlips = (await this.uow.getPackingSlipInfos()) as any;
        packingSlips.forEach((slip) => {
            slip.createDate = moment(slip.createDate).format('MM/DD/YYYY');
            slip.minDelivered = slip.minDelivered ? moment(slip.minDelivered).format('MM/DD/YYYY') : null;
        });
        this.packingSlips = packingSlips;
    }

    async getRecentData() {
        const recentData = (await this.uow.getRecentInvoiceAndPackSlips()) as any;
        recentData.forEach((data) => {
            data.created = moment(data.date).toDate();
        });
        this.recentData = recentData;
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.hasNonInvoicedDetails || e.data.freightNotInvoiced) {
                e.rowElement.className = e.rowElement.className + ' needs-invoice';
            } else if (!e.data.hasUndeliveredDetails) {
                e.rowElement.className = e.rowElement.className + ' needs-signed-packslip';
            }
        }
    }

    onCellPrepared(e) {
        if (e.rowType !== 'data') return;
        const data = e.data as any;
        if (e.column.dataField === 'unCompletedDetails') {
            if (data.unCompletedDetails > 0) {
                e.cellElement.className = e.cellElement.className + ' uncompleted-details';
            }
        }
    }

    async editPackSlip() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length !== 1) return;
        const packSlip = await this.uow.packingSlips.byId(selectedRows[0].id);

        const modalRef = this.modalService.show(PackingSlipDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                packingSlip: packSlip,
                disableSlipNumber: true,
                mode: 'edit',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async print() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const entity = (await this.uow.packingSlips.byId(selectedRow.id)) as PackingSlip;
        if (!entity) return;
        if (entity.reportGuid === null) {
            entity.reportGuid = crypto.randomUUID();
            await this.uow.commit([entity]);
        }
        this.reportService.openPackingSlip([entity.reportGuid]);

        // if (selectedRow.type === 'Invoice') {
        //     const entity = (await this.uow.invoices.byId(selectedRow.id)) as Invoice;
        //     if (!entity) return;
        //     if (entity.reportGuid === null) {
        //         entity.reportGuid = crypto.randomUUID();
        //         await this.uow.commit([entity]);
        //     }
        //     this.reportService.openInvoiceReport([entity.reportGuid]);
        // } else if (selectedRow.type === 'PackSlip') {
        //     const entity = (await this.uow.packingSlips.byId(selectedRow.id)) as PackingSlip;
        //     if (!entity) return;
        //     if (entity.reportGuid === null) {
        //         entity.reportGuid = crypto.randomUUID();
        //         await this.uow.commit([entity]);
        //     }
        //     this.reportService.openPackingSlip([entity.reportGuid]);
        // }
    }

    async email(selectedRow: any) {
        if (!selectedRow) return;
        if (selectedRow.type === 'Invoice') {
            const entity = (await this.uow.invoices.byId(selectedRow.id, 'dealer')) as Invoice;
            await this.invoiceService.emailInvoice(entity);
        }
    }

    openInvoice(selectedRow: any) {
        if (selectedRow.type === 'Invoice') {
            this.router.navigate([`invoices/${selectedRow.id}`]);
        }
    }

    async openInvoiceNumber(invoiceNumber: number) {
        const entity = await this.uow.getInvoiceByInvoiceNumber(invoiceNumber);
        if (!entity) return;
        this.router.navigate([`/invoices/${entity.id}`]);
    }

    async createInvoice() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const dealerId = selectedRows[0].dealerId;
        const areSameDealer = selectedRows.every((x) => dealerId === x.dealerId);
        if (!areSameDealer) {
            await alert('Cannot create invoice for different dealers', this.appInfo.settings.company);
            return;
        }
        const ids = selectedRows.map((x) => x.id);
        this.invoiceService.createInvoice(ids, 'packingSlip');
    }

    async markComplete() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        let details = await this.uow.getOrderLineDetailsByPackSlipId(selectedRow.id);

        const result = await confirm(
            `Mark all Build Details for PackSlip# ${selectedRow.packingSlipNumber} -- ${selectedRow.companyName} as Complete?`,
            `${this.appInfo.settings.company}`,
        );
        if (!result) return;

        const success = await this.checkDelivered(details);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        details.forEach((detail) => {
            if (!detail.delivered) {
                detail.delivered = new Date();
                this.entitiesService.createOrderLineDetailNote(detail.id, `Delivered date set`, this.auth.currentUser.id);
            }
            detail.statusId = OrderLineDetail.Statuses.Complete;
            this.entitiesService.createOrderLineDetailNote(detail.id, `Status Changed to Complete`, this.auth.currentUser.id);
        });
        await this.uow.commit();

        await this.utility.setOrdersCompleteByInvoiceIds([selectedRow.id]);
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async checkDelivered(details: OrderLineDetail[]) {
        const allDelivered = details.every((detail) => detail.delivered);
        if (allDelivered) return true;

        const success = await confirm(`Are you sure you want to mark this Invoice's details as Complete? Not all of the items have been delivered.`, this.appInfo.settings.company);
        if (!success) return false;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Enter Reason',
                dialogPrompt: `Please enter the reason you are marking this Invoice's details as Complete`,
                mode: 'text',
            },
        });

        const promise = new Promise<boolean>(async (resolve) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result) {
                    if (!result.value) {
                        await alert(`A reason is required. The Invoice's details were not marked as Complete.`, this.appInfo.settings.company);
                        resolve(false);
                    }

                    details
                        .filter((detail) => !detail.delivered)
                        .forEach((detail) => {
                            this.entitiesService.createOrderLineDetailNote(
                                detail.id,
                                `Completed Invoice with undelivered items. Reason: ${result.value}`,
                                this.auth.currentUser.id,
                            );
                        });
                    await this.uow.commit();
                    resolve(true);
                }
                modalRef.hide();
                resolve(false);
            });
        });
        return promise;
    }

    async openBuildDetailsAsDialog(cell: any) {
        const modalRef = this.modalService.show(OrderBuildDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                packingSlipId: cell.data.id,
                showAsDialog: true,
                storageKey: 'packslip-orderlinedetail-grid',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.dataGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }
}
